<template>
  <div class="campaign-step">
    <!-- Blocked: brand not approved yet -->
    <collapsible-section v-if="state === 'blocked'" :collapsible="false" title="Waiting on brand approval" icon="tim-icons icon-time-alarm">
      <div class="campaign-step__status">
        <span class="campaign-step__pulse"></span>
        <p class="campaign-step__lead mb-0">The campaign can be registered once your brand is approved. We check for updates automatically.</p>
      </div>
    </collapsible-section>

    <!-- In review -->
    <collapsible-section v-else-if="state === 'review'" :collapsible="false" title="Campaign under review" icon="tim-icons icon-bullet-list-67">
      <div class="campaign-step__status">
        <span class="campaign-step__pulse"></span>
        <div>
          <p class="campaign-step__lead">Your campaign is being verified by the registry. This usually takes several days, and can take up to two weeks when the registry is busy.</p>
          <p class="campaign-step__hint mb-0">We check for updates every few minutes and this page refreshes automatically.</p>
        </div>
      </div>
    </collapsible-section>

    <!-- Complete -->
    <collapsible-section v-else-if="state === 'complete'" :collapsible="false" title="Campaign verified" icon="tim-icons icon-check-2">
      <p class="campaign-step__lead mb-1">Your campaign is registered and verified.</p>
      <p class="campaign-step__hint mb-0" v-if="settings && settings.a2p_campaign_sid">Campaign id: {{ settings.a2p_campaign_sid }}</p>
    </collapsible-section>

    <!-- Ready or rejected: the form -->
    <template v-else>
      <collapsible-section v-if="state === 'rejected'" :collapsible="false" title="Campaign rejected" icon="tim-icons icon-alert-circle-exc">
        <p class="campaign-step__lead">The registry rejected your campaign with the following feedback:</p>
        <div class="campaign-step__feedback">{{ feedback }}</div>
        <p class="campaign-step__hint mb-0">
          Adjust the details below and resubmit. Resubmitting is free, but each review
          takes time, so it is worth matching the guidance above closely before resubmitting.
        </p>
      </collapsible-section>

      <collapsible-section title="Compliance pages" icon="tim-icons icon-single-copy-04">
        <info-hint icon="icon-alert-circle-exc" variant="warning">
          The registry checks these pages before approving. Both must be publicly reachable without logging in, and must state that mobile numbers and text messaging consent are never shared or sold to third parties for marketing, along with how to opt out by replying STOP.
        </info-hint>
        <div class="row">
          <div class="col-lg-6">
            <base-input
              label="Privacy policy URL"
              placeholder="https://www.yourbusiness.com/privacy-policy"
              :error="fieldErrors.privacy_policy_url"
              v-model="localData.privacy_policy_url"
            />
          </div>
          <div class="col-lg-6">
            <base-input
              label="Terms of service URL"
              placeholder="https://www.yourbusiness.com/terms-of-service"
              :error="fieldErrors.terms_url"
              v-model="localData.terms_url"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-10">
            <base-input
              label="Opt-in evidence URL (recommended)"
              placeholder="https://www.yourbusiness.com/sms-opt-in.html"
              :error="fieldErrors.opt_in_evidence_url"
              v-model="localData.opt_in_evidence_url"
            />
            <p class="campaign-step__hint">
              A public page showing the consent checkbox as customers see it, with the wording
              repeated as text and a screenshot. Reviewers verify consent manually, and our
              booking flow puts the checkbox several steps in, so a page they can open directly
              is the difference between an approval and a rejection for unverifiable consent.
              We add this link to your submission automatically.
            </p>
          </div>
        </div>
      </collapsible-section>

      <collapsible-section title="Campaign details" icon="tim-icons icon-bullet-list-67">
        <info-hint icon="icon-coins" variant="neutral">
          Campaign registration is covered by the $20 activation fee already on your bill.
          Once your number is active, SMS costs $10 per month per number, including 250 SMS credits,
          plus 3¢ per additional credit. Sent and received messages both use credits. Editing and resubmitting a rejected campaign is free.
        </info-hint>
        <div class="row">
          <div class="col-lg-10">
            <base-input
              label="How do customers opt in?"
              :error="fieldErrors.opt_in_description"
            >
              <base-textarea
                :rows="3"
                :maxlength="2048"
                v-model="localData.opt_in_description"
              />
            </base-input>
          </div>
        </div>
      </collapsible-section>

      <collapsible-section title="Sample messages" icon="tim-icons icon-chat-33">
        <info-hint icon="icon-badge" variant="neutral">
          The registry requires at least two samples and checks each one for your business name,
          opt-out wording and a rate disclosure. Filling all five gives reviewers a fuller picture.
          Show variable parts in square brackets, like [date] or [link].
        </info-hint>
        <div class="row">
          <div class="col-lg-10">
            <base-input
              v-for="n in [1, 2, 3, 4, 5]"
              :key="'cs-sample-' + n"
              :label="'Sample ' + n + (n > 2 ? ' (optional)' : '')"
              :error="n === 1 ? fieldErrors.sample_messages : ''"
            >
              <base-textarea
                :rows="2"
                :maxlength="1024"
                v-model="localData['sample_message_' + n]"
              />
            </base-input>
          </div>
        </div>
      </collapsible-section>

      <!-- Read-only on purpose. These are submitted on the tenant's behalf so the
           wording is known-compliant and consistent across every venue; letting
           tenants rewrite consent copy is what produced three rejections. -->
      <collapsible-section title="Consent replies" icon="tim-icons icon-send">
        <info-hint icon="icon-alert-circle-exc" variant="neutral">
          We submit these on your behalf. The registry requires an opt-in confirmation that
          names your business and states frequency, rates and how to stop.
        </info-hint>
        <div class="campaign-step__readonly">
          <span class="campaign-step__readonly-label">Opt-in keywords</span>
          <p class="campaign-step__readonly-value">{{ optInKeywords }}</p>
        </div>
        <div class="campaign-step__readonly">
          <span class="campaign-step__readonly-label">Opt-in confirmation message</span>
          <p class="campaign-step__readonly-value">{{ optInMessage }}</p>
        </div>
        <p class="campaign-step__hint mb-0">
          Replies of STOP, CANCEL, QUIT, UNSUBSCRIBE and END are handled automatically and
          stop all messages. HELP and INFO return your contact details.
        </p>
      </collapsible-section>

      <collapsible-section title="Confirm and submit" icon="tim-icons icon-check-2">
        <base-checkbox v-model="localData.attested">
          <span class="campaign-step__attest">
            I confirm these details are accurate, that the consent wording above appears on our
            booking form exactly as described, and that the checkbox is unchecked by default.
          </span>
        </base-checkbox>
      </collapsible-section>
    </template>
  </div>
</template>

<script>
import BaseTextarea from '@/components/Inputs/BaseTextarea.vue';
import InfoHint from '@/components/InfoHint.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection';

// Boilerplate the tenant starts from instead of composing compliance copy
// themselves. Every element here is one the registry checks for under error
// 30924: message type, frequency, "message and data rates may apply", and
// opt-out instructions, with the consent wording quoted verbatim so the
// description matches what a reviewer sees on the booking page.
const CONSENT_SENTENCE =
  'I agree to receive booking confirmations, reminders, waiver requests and receipts from ' +
  '{business} by text at the number provided. Message frequency varies, up to 4 messages per ' +
  'booking. Message and data rates may apply. Reply STOP to opt out, HELP for help. ' +
  'See our Privacy Policy and Terms of Service.';

function fill(template, business, bookingUrl) {
  return template
    .split('{business}').join(business)
    .split('{bookingUrl}').join(bookingUrl);
}

function defaultOptInDescription(business, bookingUrl) {
  return fill(
    'Customers opt in on {business}\'s online booking page at {bookingUrl}. On the booking ' +
    'form, below the customer details, there is a checkbox that is unchecked by default and ' +
    'reads: "' + CONSENT_SENTENCE + '" The customer must click the checkbox themselves and it ' +
    'is never pre-selected. Bookings can be completed without checking it, and consent is not ' +
    'a condition of purchase. A separate checkbox, also unchecked by default, covers ' +
    'promotional messages.',
    business,
    bookingUrl
  );
}

// Each carries the business name; at least one carries a rate disclosure, and
// all carry opt-out wording, which is what sample review checks for.
const DEFAULT_SAMPLES = [
  '{business}: your booking for [date] at [time] is confirmed for [number] players. Questions? Just reply to this text. Msg&data rates may apply. Reply STOP to opt out.',
  '{business}: reminder, your game starts in 2 hours. Please arrive 15 minutes early. Reply STOP to opt out, HELP for help.',
  '{business}: please sign your waiver before you arrive: [link] Msg&data rates may apply. Reply STOP to opt out.',
  '{business}: we received your payment of [amount] for your booking on [date]. Your receipt: [link] Reply STOP to opt out.',
  '{business}: thanks for your question. Yes, we can move your booking to [date] at [time]. Reply YES to confirm. Reply STOP to opt out, HELP for help.'
];

// Mirrors what sms-onboarding submits. If the lambda's optInMessage changes,
// change it here too: showing wording we do not actually send is worse than
// showing nothing.
function optInMessageFor(business) {
  return business + ': You are now subscribed to booking updates by text. Message frequency ' +
    'varies, up to 4 messages per booking. Message and data rates may apply. Reply HELP for ' +
    'help, STOP to opt out.';
}

const OPT_IN_KEYWORDS = 'START, YES, UNSTOP';

const STATE_BY_STATUS = {
  campaign_pending: 'ready',
  campaign_review: 'review',
  campaign_rejected: 'rejected',
  number_pending: 'complete',
  test_pending: 'complete',
  active: 'complete',
  suspended: 'complete'
};

export default {
  name: 'SMSOnboardingStepCampaign',
  components: {
    BaseTextarea,
    InfoHint,
    CollapsibleSection
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: null
    },
    validationErrors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    // Seed from the approved business profile. The settings prop is available
    // in data(); children mount before the parent's mounted hook, so seeding
    // must happen here, never in mounted().
    const profile = this.settings?.business_profile || {};
    const business = profile.legal_business_name || 'your business';
    let bookingUrl = 'https://offthecouch.io/book/your-code';
    try {
      const group = JSON.parse(localStorage.getItem('group') || '{}');
      if (group.code) bookingUrl = 'https://offthecouch.io/book/' + group.code;
    } catch (e) {
      // localStorage unavailable; the placeholder above still reads sensibly.
    }
    this.businessName = business;

    return {
      localData: {
        // Attestation, mirroring the confirmation Twilio's own console requires
        // before it will submit. Not a registry field: it is the tenant
        // asserting the described flow is really what their customers see.
        attested: false,
        // Not user-selectable (decided 2026-07-18): every tenant's traffic is
        // the same platform-defined mix, and under baked-in pricing the
        // campaign tier is OUR cost, not theirs. LOW_VOLUME always at
        // onboarding; upgrades to MIXED are an operational path when a tenant
        // demonstrably outgrows the cap.
        use_case: 'LOW_VOLUME',
        opt_in_description: profile.opt_in_description || defaultOptInDescription(business, bookingUrl),
        privacy_policy_url: profile.privacy_policy_url || '',
        terms_url: profile.terms_url || '',
        opt_in_evidence_url: profile.opt_in_evidence_url || '',
        sample_message_1: profile.sample_message_1 || fill(DEFAULT_SAMPLES[0], business, bookingUrl),
        sample_message_2: profile.sample_message_2 || fill(DEFAULT_SAMPLES[1], business, bookingUrl),
        sample_message_3: profile.sample_message_3 || fill(DEFAULT_SAMPLES[2], business, bookingUrl),
        sample_message_4: profile.sample_message_4 || fill(DEFAULT_SAMPLES[3], business, bookingUrl),
        sample_message_5: profile.sample_message_5 || fill(DEFAULT_SAMPLES[4], business, bookingUrl),
        ...(this.formData || {})
      }
    };
  },
  computed: {
    state() {
      const status = this.settings?.registration_status || 'not_started';
      return STATE_BY_STATUS[status] || 'blocked';
    },
    feedback() {
      return this.settings?.campaign_review_feedback || 'No detail was provided.';
    },
    optInKeywords() {
      return OPT_IN_KEYWORDS;
    },
    optInMessage() {
      return optInMessageFor(this.businessName || 'your business');
    },
    fieldErrors() {
      const map = {};
      for (const e of this.validationErrors) {
        if (e && e.field && !map[e.field]) {
          map[e.field] = e.message || 'Invalid';
        }
      }
      return map;
    }
  },
  watch: {
    localData: {
      deep: true,
      handler(newVal) {
        this.$emit('update', { ...newVal });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.campaign-step {
  &__lead {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.55;
    margin-bottom: 0.75rem;
  }

  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.5;
  }

  &__status {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  &__pulse {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-radius: 50%;
    background: #e14eca;
    box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.18);
    animation: campaign-step-pulse 1.8s ease-in-out infinite;
  }

  &__readonly {
    margin-bottom: 0.875rem;
  }

  &__readonly-label {
    display: block;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 0.25rem;
  }

  &__readonly-value {
    font-size: 0.8125rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(125, 125, 125, 0.12);
    border-radius: 0.375rem;
    padding: 10px 14px;
    margin-bottom: 0;
  }

  &__attest {
    font-size: 0.8125rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.8);
  }

  &__feedback {
    white-space: pre-wrap;
    font-size: 0.8125rem;
    line-height: 1.55;
    color: #fd77a4;
    background: rgba(253, 93, 147, 0.08);
    border-left: 3px solid #fd5d93;
    border-radius: 0 0.375rem 0.375rem 0;
    padding: 12px 16px;
    margin-bottom: 0.75rem;
  }
}

@keyframes campaign-step-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.18); }
  50% { box-shadow: 0 0 0 7px rgba(225, 78, 202, 0.08); }
}
</style>

<style>
.white-content .campaign-step__lead { color: rgba(0, 0, 0, 0.8); }
.white-content .campaign-step__hint { color: rgba(0, 0, 0, 0.6); }
.white-content .campaign-step__readonly-label { color: rgba(0, 0, 0, 0.6); }
.white-content .campaign-step__readonly-value { color: rgba(0, 0, 0, 0.85); }
.white-content .campaign-step__attest { color: rgba(0, 0, 0, 0.8); }
.white-content .campaign-step__feedback {
  color: #c81e58;
  background: rgba(253, 93, 147, 0.08);
}
</style>
